import { DeleteOutlined, LockOutlined, PlusOutlined, SaveOutlined } from "@ant-design/icons";
import {
  Button,
  DatePicker,
  Divider,
  Select,
  Space,
  Spin,
  Tooltip
} from "antd";
import "@styles/styles-Settings.css";
import dayjs from "dayjs";
import { use, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createSettingsDataPoint,
  deleteSettingsDataPoint,
  getSettingsDataPoint
} from "../../store/actions/settings.actions";
import { DATE_FORMAT } from "../../Utils/date_utils";
import { OptionGroupSelect } from '../Compare/Components_1v1/OptionGroupSelect';
import { OverrideDatapointField } from "./OverrideDatapointField";
import "./styles.css";
import { 
  PermissionedSelect, 
  PermissionedDatePicker, 
  PermissionedButton,
  useDataAccess
} from "../PermissionedComponents";
import PermissionViewable from "../PermissionedComponents/PermissionViewable";
import { DATA_STREAM_PERMISSIONS } from "@utils/data_access";

const OVERRIDE_DATAPOINT_TYPES = [
  { value: "gps", label: "GPS Data" },
  { value: "forceplate", label: "Forceplate Data" },
  { value: "testing", label: "Testing Data" },
  { value: "nutrition", label: "Nutrition Data" },
  { value: "cognition", label: "Cognition Data" },
];

const { Option } = Select;

export const OverrideDatapoint = () => {
  const dispatch = useDispatch();
  const {
    datapoint,
    loading: { datapoint: loading },
    pending,
    avg,
  } = useSelector(({ settings }) => settings);

  const {
    positions,
    playerGroups: groups,
    players,
		all_metric_types
	} = useSelector(({ team }) => team);

  const [type, setType] = useState("gps");
  const [date, setDate] = useState(dayjs());
  const [playerPosGroupOptions, setPlayerPosGroupOptions] = useState([]);
  const [selectedPlayers, setSelectedPlayers] = useState([])
  const [metric, setMetric] = useState();
  const [inputs, setInputs] = useState({});
  const [updateRPE, setUpdateRPE] = useState(false);

  const { canOverride, canDelete, ready: permissionsReady } = useDataAccess();

  const overrideTypes = OVERRIDE_DATAPOINT_TYPES.map((option) => ({
    ...option,
    disabled: !canOverride(option.value),
  }));

  // "gps" is only the initial guess. Once permissions resolve, move off it if the
  // user cannot override it, so the page doesn't open onto a fully disabled form.
  useEffect(() => {
    if (!permissionsReady || canOverride(type)) return;
    const firstAllowed = OVERRIDE_DATAPOINT_TYPES.find(({ value }) => canOverride(value));
    if (firstAllowed) setType(firstAllowed.value);
  }, [permissionsReady, type]);

  const overridePermission = DATA_STREAM_PERMISSIONS[type]?.override ?? null;
  const deletePermission = DATA_STREAM_PERMISSIONS[type]?.remove ?? null;

  const emptyData = useMemo(() => {
    if (type === "gps") {
      if(datapoint === null || datapoint === undefined) {
        return true;
      }
      
      if(datapoint?.data?.length > 0 && datapoint?.metrics?.length > 0) {
        // Checking only if assigned metric values are null. If metric id is null meaning it is a derived metric. 
        // Filtering such derived metrics to get actual assigned metrics and checking if the data exists for those metrics.
        const filteredMetrics = datapoint?.metrics.filter(({ metric_id }) => metric_id !== undefined);
        return filteredMetrics?.every(({ key }) => datapoint?.data[0][key] === null);
      };  
      
      return true;
    }
    else {
      if(datapoint === null || datapoint === undefined) {
        return true;
      }

      return false
    }      
  }, [datapoint]);

  useEffect(() => {
    if (type === "gps") {
      setInputs(datapoint?.data?.[0] || {});
    } else {
      if (metric?.unique_identifier) {
        setInputs({ [metric.unique_identifier]: datapoint });
      }
    }
  }, [datapoint, metric]);

  const handleSearchDatapoint = () => {
    if (!date) return;
    if (type !== "gps" && !metric) return;
    dispatch(
      getSettingsDataPoint(
        type,
        date?.format(DATE_FORMAT),
        selectedPlayers,
        metric
      )
    );
  };

  useEffect(() => {
    handleSearchDatapoint();
  }, [type, date, selectedPlayers, metric]);

  useEffect(() => {
    setInputs({});
    setMetric();
  }, [type]);

  useEffect(() => {
    let groupOptions = [], metricObj = {};
    metricObj["label"] = <span>Team</span>;
    metricObj["title"] = "Team";
    metricObj["options"] = [{
      label: <span>Full Team</span>,
      value: "full-team",
      key: "full-team"
    }];
    
    groupOptions.push(metricObj);

    if (players.length > 0) {
      groupOptions.push(getOptionGroup("Players", players, "player"));
    }

    if (positions.length > 0) {
      groupOptions.push(getOptionGroup("Positions", positions, "pos"));
    }

    if (groups.length > 0) {
      groupOptions.push(getOptionGroup("Groups", groups, "group"));
    }

    setPlayerPosGroupOptions(groupOptions);
    setSelectedPlayers([]);
  }, [positions, groups, players])

  const handleChangeInput = (key) => (value) => {
    setInputs({ ...inputs, [key]: +value });
  };

  const handleCreateDatapoint = () => {
    if (!selectedPlayers.length || !date) return;
    if (type === "gps") {
      if ("total_time" in inputs && (inputs["total_time"] === null || inputs["total_time"] === undefined)) {
        inputs["total_time"] = 0;
      }
      if ("session_RPE" in inputs && (inputs["session_RPE"] === null || inputs["session_RPE"] === undefined)) {
        inputs["session_RPE"] = 0;
      }
    }
    dispatch(
      createSettingsDataPoint(
        type,
        date.format(DATE_FORMAT),
        selectedPlayers,
        inputs,
        metric,
        updateRPE
      )
    );
  };

  const handleDeleteDatapoint = () => {
    if (!selectedPlayers.length || !date) return;
    dispatch(
      deleteSettingsDataPoint(type, date.format(DATE_FORMAT), selectedPlayers, metric)
    );
  };

  const needsMetric = type !== "gps" && !metric;

  const actionsDisabled = useMemo(() => {
    return !selectedPlayers.length || !date || needsMetric;
  }, [date, selectedPlayers, needsMetric]);

  const hasInputValues = Object.values(inputs).some((value) => value !== null && value !== undefined);

  const metricHint = needsMetric ? "Choose a metric first." : undefined;
  const saveHint = metricHint ?? (hasInputValues ? undefined : "Enter at least one value to save.");

  const fillWithAverage = (type) => (key, identifier) => {
    if (avg[type] && avg[type][key] !== undefined)
      setInputs({ ...inputs, [type === "gps" ? key : identifier]: avg[type][key]?.toFixed(2) });
  };

  const revertChange = (key, identifier) => {
    const data = datapoint.data?.[0] || {};
    setInputs({ ...inputs, [type === "gps" ? key : identifier]: data[key] || datapoint });
  };

  const getOptionGroup = (optionGroupName, JSONOptions, metric_type) => {
    let metricObj = {}, metricOps = [];
    metricObj["label"] = <span>{optionGroupName}</span>;
    metricObj["title"] = optionGroupName;
    
    for (let i = 0; i < JSONOptions.length; i++) {
      const metric = JSONOptions[i];
      if (["gps", "forceplate", "testing", "wellness"].includes(metric_type)) {
        metricOps.push({
          label: <span>{metric["name"]}</span>,
          value: `${metric_type}-${metric["metric_id"]}`,
          key: metric["metric_id"]
        });
      }
      else if (metric_type === "player") {
        metricOps.push({
          label: <span>{`${metric["first_name"]} ${metric["last_name"]}`}</span>,
          value: `${metric_type}-${metric["player_id"]}`,
          key: metric["player_id"]
        });
      }
      else if (metric_type === "pos") {
        metricOps.push({
          label: <span>{metric}</span>,
          value: `${metric_type}-${metric}`,
          key: metric
        });
      }
      else if (metric_type === "group") {
        metricOps.push({
          label: <span>{metric["name"]}</span>,
          value: `${metric_type}-${metric["player_group_id"]}`,
          key: metric["player_group_id"]
       });
      }
    }

    if (metric_type === "gps") {
      metricObj["options"] = metricOps;
    } else {
      metricObj["options"] = metricOps.sort((a, b) => {
        const valueA = a?.label?.props?.children || "";
        const valueB = b?.label?.props?.children || "";
        return valueA.localeCompare(valueB);
      });
    }
    return metricObj;
  }

  const handleOptionGroupChange = (value) => {
    setSelectedPlayers(value);
  }

  return (
    <PermissionViewable
      permissionCheck="OverrideDataPoints"
      permissionMessage="You don't have permission to override datapoints"
      tooltipMessage="Datapoint override requires special permissions"
    >
      <div className="override-datapoint-form">
          <div className="team-settings-input-wrap settings-first-field settings-inline-field settings-inline-field-half">
            <div className="team-settings-input-label">Data type</div>
            <Select
              placeholder="Choose Data Type"
              showSearch
              optionFilterProp="value"
              value={type}
              className="team-settings-input"
              onChange={setType}
            >
              {overrideTypes.map((type) => (
                <Option
                  key={type.value}
                  value={type.value}
                  disabled={type.disabled}
                >
                  <span style={{ display: "flex", justifyContent: "space-between" }}>
                    {type.label}
                    {type.disabled && <LockOutlined />}
                  </span>
                </Option>
              ))}
            </Select>
          </div>
          <div className="team-settings-input-wrap settings-inline-field settings-inline-field-half">
            <div className="team-settings-input-label">Date</div>
            <PermissionedDatePicker
              className="team-settings-input"
              value={date}
              onChange={setDate}
              permissionCheck={overridePermission}
            />
          </div>
          <div className="team-settings-input-wrap settings-inline-field settings-inline-field-half">
            <div className="team-settings-input-label">Players</div>
            <div className="player-group-pos-container">
              <OptionGroupSelect 
                className="player-group-pos-left" 
                selectedOptionVal={selectedPlayers}
                options={playerPosGroupOptions}
                placeholder="Select Players"
                width="100%"
                handleOptionGroupChange={handleOptionGroupChange}
              />
            </div>
          </div>
          {type !== "gps" && (
            <div className="team-settings-input-wrap settings-inline-field settings-inline-field-half">
              <div className="team-settings-input-label">Metric</div>
              <PermissionedSelect
                placeholder="Choose Metric"
                value={metric}
                className="team-settings-input"
                onChange={(value) =>
                  setMetric(
                    all_metric_types[type]?.find(({ unique_identifier }) => value === unique_identifier)
                  )
                }
                permissionCheck={overridePermission}
              >
                {all_metric_types[type]?.map((metric) => (
                  <PermissionedSelect.Option key={metric.unique_identifier} value={metric.unique_identifier}>
                    {metric.name}
                  </PermissionedSelect.Option>
                ))}
              </PermissionedSelect>
            </div>
          )}
          <Divider />
          <Spin tip="Loading..." spinning={loading}>
            {type === "gps" && selectedPlayers?.length
              ? datapoint?.metrics?.map((metric) => {
                  return (
                    <OverrideDatapointField
                      metric={metric}
                      key={metric.key}
                      avg={avg}
                      value={inputs[metric.key]}
                      handleChange={handleChangeInput(metric.key)}
                      handleUpdateRPE={setUpdateRPE}
                      updateRPE={updateRPE}
                      fillWithAverage={fillWithAverage}
                      revertChange={revertChange}
                      type={type}
                      metricIdentifier={metric.key}
                      has_rpe={datapoint?.has_rpe}
                    />
                  );
                })
              : !!metric && !!selectedPlayers?.length && (
                  <OverrideDatapointField
                    metric={metric}
                    avg={avg}
                    value={inputs[metric.unique_identifier]}
                    handleChange={handleChangeInput(metric.unique_identifier)}
                    fillWithAverage={fillWithAverage}
                    revertChange={revertChange}
                    type={type}
                    metricIdentifier={metric.unique_identifier}
                  />
                )}
            { selectedPlayers?.length ? (
              <div className="team-settings-input-wrap">
                <Space>
                  <Tooltip title={canOverride(type) ? saveHint : undefined}>
                    <PermissionedButton
                      icon={<SaveOutlined />}
                      type="primary"
                      onClick={handleCreateDatapoint}
                      loading={pending.create}
                      disabled={actionsDisabled || !hasInputValues}
                      permissionCheck={overridePermission}
                    >
                      Save Datapoint
                    </PermissionedButton>
                  </Tooltip>
                  <Tooltip title={canDelete(type) ? metricHint : undefined}>
                    <PermissionedButton
                      icon={<DeleteOutlined />}
                      danger
                      onClick={handleDeleteDatapoint}
                      disabled={actionsDisabled}
                      loading={pending.delete}
                      permissionCheck={deletePermission}
                    >
                      Delete Datapoint
                    </PermissionedButton>
                  </Tooltip>
                </Space>
              </div>
            ) : null}
          </Spin>
      </div>
    </PermissionViewable>
  );
};
